import React from 'react';

/**
 * ImageMeta — SEO-first image wrapper.
 *
 * Every image on the marketing site should render through this so Google
 * gets the full metadata bundle it needs:
 *   - Descriptive alt (mandatory prop)
 *   - Explicit width + height (so CLS = 0 and Search Console can index)
 *   - loading + decoding hints
 *   - Optional Schema.org ImageObject JSON-LD (opt-in via `schema` prop)
 *
 * Prefer this over bare <img> anywhere the image is content, not decoration.
 * If it's decoration, pass alt="" and skip schema.
 *
 * For Unsplash URLs we auto-build a `srcSet` at 640/960/1400/1800w so the
 * browser pulls the smallest render that fits — great LCP, big data win on
 * mobile.
 */

function unsplashAt(url, width) {
  if (!url) return url;
  try {
    const u = new URL(url);
    if (!/images\.unsplash\.com/.test(u.hostname)) return url;
    u.searchParams.set('w', String(width));
    if (!u.searchParams.get('auto')) u.searchParams.set('auto', 'format');
    if (!u.searchParams.get('fit')) u.searchParams.set('fit', 'crop');
    if (!u.searchParams.get('q')) u.searchParams.set('q', '75');
    return u.toString();
  } catch (e) {
    return url;
  }
}

export default function ImageMeta({
  src,
  alt, // REQUIRED — descriptive alt text
  width,
  height,
  loading = 'lazy',
  decoding = 'async',
  fetchPriority,
  className = '',
  sizes,
  caption, // optional visible caption
  credit, // optional visible credit line
  schema, // opt-in: emit <script type="application/ld+json"> ImageObject
  license = 'https://unsplash.com/license', // default: Unsplash Free License
  creator,
  responsive = true, // auto-build Unsplash srcSet
  testId,
  ...rest
}) {
  if (alt === undefined) {
    // Fail loud in dev so we never ship a mystery image.
    // eslint-disable-next-line no-console
    console.warn('[ImageMeta] Missing alt prop for', src);
  }

  const isUnsplash = typeof src === 'string' && src.includes('images.unsplash.com');
  const srcSet = responsive && isUnsplash
    ? [640, 960, 1400, 1800].map((w) => `${unsplashAt(src, w)} ${w}w`).join(', ')
    : undefined;

  const img = (
    <img
      src={isUnsplash ? unsplashAt(src, 1400) : src}
      srcSet={srcSet}
      sizes={sizes || (srcSet ? '(max-width: 640px) 100vw, (max-width: 1024px) 90vw, 1120px' : undefined)}
      alt={alt || ''}
      width={width}
      height={height}
      loading={loading}
      decoding={decoding}
      {...(fetchPriority ? { fetchpriority: fetchPriority } : {})}
      className={className}
      data-testid={testId}
      {...rest}
    />
  );

  const ld = schema && src && alt
    ? {
        '@context': 'https://schema.org',
        '@type': 'ImageObject',
        contentUrl: isUnsplash ? unsplashAt(src, 1800) : src,
        url: isUnsplash ? unsplashAt(src, 1800) : src,
        description: alt,
        name: alt,
        width: width || undefined,
        height: height || undefined,
        license,
        acquireLicensePage: license,
        creditText: credit || (isUnsplash ? 'Unsplash' : undefined),
        creator: creator ? { '@type': 'Person', name: creator } : undefined,
        copyrightNotice: credit || undefined,
      }
    : null;

  if (!caption && !credit && !ld) return img;

  return (
    <>
      {img}
      {(caption || credit) && (
        <figcaption className="mt-2 text-xs text-[#64748B]" data-testid={testId ? `${testId}-caption` : undefined}>
          {caption}
          {caption && credit ? ' · ' : ''}
          {credit && <span className="text-[#94A3B8]">{credit}</span>}
        </figcaption>
      )}
      {ld && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }}
        />
      )}
    </>
  );
}

export { unsplashAt };
